/**
 * 売買検索一覧の検索結果レコメンド
 * レコメンド表示枠が画面内に入ったタイミングでAPIを呼び出して本体をAjax取得し、枠内に差し込む。
 */
const SEARCH_RESULT_RECOMMEND_OBSERVED_ATTR = "data-search-result-recommend-observed";
const SEARCH_RESULT_RECOMMEND_CAROUSEL_ATTR = "data-search-result-recommend-carousel";
const SEARCH_RESULT_RECOMMEND_CAROUSEL_INITIALIZED_ATTR = "data-search-result-recommend-carousel-initialized";
const SEARCH_RESULT_RECOMMEND_VISIBLE_CARD_COUNT = 2;

// 監視中（まだ画面内に入っていない）の枠。非同期更新でDOMから外れた枠の監視解除に使用する
const pendingLoadFrames = new Set<HTMLElement>();
// 同じURLを使う複数の枠(パターンB)でAPI呼び出しを1回にするため、レスポンスHTMLのPromiseをURL単位で共有する
const recommendHtmlCacheByUrl = new Map<string, Promise<string>>();

const SEARCH_RESULT_RECOMMEND_FRAME_SELECTOR = "[data-search-result-recommend-frame]";

// 非同期更新でDOMから外れた要素をObserverに残さないよう、監視中の要素を保持する
const pendingInviewFrames = new Set<Element>();
const pendingInviewContents = new Set<Element>();

// 再描画で同じ要素が再び監視されても、画面内表示イベントを二重送信しない
const sentInviewFrames = new WeakSet<Element>();
const sentInviewContents = new WeakSet<Element>();

function getBuyTracking(): any {
  return window.Nifty?.Buy?.Tracking;
}

function generateRecommendFrameId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** 画面内表示イベントは要素ごとに1回だけ送るため、交差後は監視を解除し計測済みを記録する。 */
function createInviewEventObserver(
  elementsAwaitingInviewEvent: Set<Element>,
  elementsWhoseInviewEventWasSent: WeakSet<Element>,
  sendInviewEvent: (recommendFrame: HTMLElement) => void,
): IntersectionObserver {
  const observer: IntersectionObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || entry.intersectionRatio <= 0) {
        continue;
      }
      const observedElement = entry.target;
      observer.unobserve(observedElement);
      elementsAwaitingInviewEvent.delete(observedElement);
      if (elementsWhoseInviewEventWasSent.has(observedElement)) {
        continue;
      }
      const recommendFrame = observedElement.closest<HTMLElement>(SEARCH_RESULT_RECOMMEND_FRAME_SELECTOR);
      if (recommendFrame == null) {
        continue;
      }
      elementsWhoseInviewEventWasSent.add(observedElement);
      sendInviewEvent(recommendFrame);
    }
  });
  return observer;
}

const frameInviewEventObserver = createInviewEventObserver(pendingInviewFrames, sentInviewFrames, (recommendFrame) => {
  getBuyTracking()?.sendSearchResultRecommendFrameInview(recommendFrame);
});
const contentsInviewEventObserver = createInviewEventObserver(pendingInviewContents, sentInviewContents, (recommendFrame) => {
  getBuyTracking()?.sendSearchResultRecommendContentsInview(recommendFrame);
});

function startInviewObservation(observer: IntersectionObserver, elementsAwaitingInviewEvent: Set<Element>, observedElement: Element): void {
  elementsAwaitingInviewEvent.add(observedElement);
  observer.observe(observedElement);
}

const recommendLoadTriggerObserver = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (!entry.isIntersecting) {
      continue;
    }
    const frame = entry.target as HTMLElement;
    recommendLoadTriggerObserver.unobserve(frame);
    pendingLoadFrames.delete(frame);
    loadSearchResultRecommend(frame);
  }
}, {
  // 枠が見えた時点でレコメンドを描画済みにするため、物件カード1枚分手前から先読みする。
  rootMargin: "0px 0px 680px 0px",
});

/** 検索条件変更等で枠が差し替わった後も、旧枠の監視とfetchが残り続けないようにする。 */
function cleanupDetachedFrames(): void {
  pendingLoadFrames.forEach((frame) => {
    if (!frame.isConnected) {
      recommendLoadTriggerObserver.unobserve(frame);
      pendingLoadFrames.delete(frame);
    }
  });

  const inviewObservationPairs: Array<[Set<Element>, IntersectionObserver]> = [
    [pendingInviewFrames, frameInviewEventObserver],
    [pendingInviewContents, contentsInviewEventObserver],
  ];
  inviewObservationPairs.forEach(([elementsAwaitingInviewEvent, observer]) => {
    elementsAwaitingInviewEvent.forEach((observedElement) => {
      if (!observedElement.isConnected) {
        observer.unobserve(observedElement);
        elementsAwaitingInviewEvent.delete(observedElement);
      }
    });
  });
}

/** 非同期更新のたびに呼ばれるため、監視済みの枠は属性で除外して二重監視を防ぐ。 */
function initBuySearchResultRecommend(): void {
  cleanupDetachedFrames();

  const frames = document.querySelectorAll<HTMLElement>(
    `${SEARCH_RESULT_RECOMMEND_FRAME_SELECTOR}:not([${SEARCH_RESULT_RECOMMEND_OBSERVED_ATTR}])`
  );
  frames.forEach((frame) => {
    frame.setAttribute(SEARCH_RESULT_RECOMMEND_OBSERVED_ATTR, "true");
    // 再描画で同じURLの枠が作られても、前回の取得結果を再利用しない
    recommendHtmlCacheByUrl.delete(frame.dataset.url ?? "");
    if (!frame.dataset.recommendFrameId) {
      frame.dataset.recommendFrameId = generateRecommendFrameId();
    }
    pendingLoadFrames.add(frame);
    recommendLoadTriggerObserver.observe(frame);
    startInviewObservation(frameInviewEventObserver, pendingInviewFrames, frame);
  });

  initSearchResultRecommendCarousel(document);
}

/** スクロールできない2件以下では、押せないナビが残らないよう非表示にする。 */
function initSearchResultRecommendCarousel(root: ParentNode): void {
  const carousels = root.querySelectorAll<HTMLElement>(`[${SEARCH_RESULT_RECOMMEND_CAROUSEL_ATTR}]`);

  carousels.forEach((carousel) => {
    if (carousel.hasAttribute(SEARCH_RESULT_RECOMMEND_CAROUSEL_INITIALIZED_ATTR)) {
      return;
    }

    // パターンCは10件ずつ2行に分けて表示するため、全行を同じ量だけ連動してスクロールする。
    const cardRows = Array.from(carousel.querySelectorAll<HTMLElement>("[data-search-result-recommend-list]"));
    const firstCardRow = cardRows[0];
    const nav = carousel.querySelector<HTMLElement>("[data-search-result-recommend-nav]");
    const prevButton = carousel.querySelector<HTMLButtonElement>("[data-search-result-recommend-prev]");
    const nextButton = carousel.querySelector<HTMLButtonElement>("[data-search-result-recommend-next]");

    if (!firstCardRow || !nav || !prevButton || !nextButton) {
      return;
    }

    carousel.setAttribute(SEARCH_RESULT_RECOMMEND_CAROUSEL_INITIALIZED_ATTR, "true");

    const cardCount = firstCardRow.children.length;
    if (cardCount <= SEARCH_RESULT_RECOMMEND_VISIBLE_CARD_COUNT) {
      nav.hidden = true;
      return;
    }

    nav.hidden = false;

    const getOneCardScrollWidth = (): number => {
      const firstCard = firstCardRow.firstElementChild as HTMLElement | null;
      if (!firstCard) {
        return firstCardRow.clientWidth;
      }

      const firstCardWidth = firstCard.getBoundingClientRect().width;
      const rowStyle = window.getComputedStyle(firstCardRow);
      const gapValue = rowStyle.columnGap || rowStyle.gap || "0";
      const gapWidth = parseFloat(gapValue) || 0;

      return firstCardWidth + gapWidth;
    };

    const updateNavState = (): void => {
      const maxScrollLeft = firstCardRow.scrollWidth - firstCardRow.clientWidth;
      const currentScrollLeft = firstCardRow.scrollLeft;

      prevButton.disabled = currentScrollLeft <= 1;
      nextButton.disabled = currentScrollLeft >= maxScrollLeft - 1;
    };

    const scrollByOneCard = (direction: 1 | -1): void => {
      const nextScrollLeft = firstCardRow.scrollLeft + getOneCardScrollWidth() * direction;
      cardRows.forEach((row) => {
        row.scrollLeft = nextScrollLeft;
      });
      updateNavState();
    };

    prevButton.addEventListener("click", (event) => {
      if (prevButton.disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      scrollByOneCard(-1);
    });

    nextButton.addEventListener("click", (event) => {
      if (nextButton.disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      scrollByOneCard(1);
    });

    firstCardRow.addEventListener("scroll", () => {
      updateNavState();
    }, { passive: true });

    window.addEventListener("resize", () => {
      updateNavState();
    });

    updateNavState();
  });
}

// 0件・失敗時は空の枠が余白として残らないよう、監視を解除して枠ごと削除する
function removeRecommendFrame(frame: HTMLElement): void {
  frameInviewEventObserver.unobserve(frame);
  pendingInviewFrames.delete(frame);
  frame.remove();
}

function getRecommendResponseHtml(url: string): Promise<string> {
  let responseHtmlPromise = recommendHtmlCacheByUrl.get(url);
  if (responseHtmlPromise == null) {
    responseHtmlPromise = fetchRecommendResponseHtml(url);
    recommendHtmlCacheByUrl.set(url, responseHtmlPromise);
  }
  return responseHtmlPromise;
}

/** 1レスポンスに複数枠分が含まれるため、指定した枠番号の結果HTMLだけを返す。 */
function extractPlacementResultsHtml(responseHtml: string, placementNumber: string): string {
  const responseHtmlTemplate = document.createElement("template");
  responseHtmlTemplate.innerHTML = responseHtml;
  const placementResultsElement = responseHtmlTemplate.content.querySelector<HTMLElement>(
    `[data-recommend-results-placement-number="${placementNumber}"]`
  );
  return placementResultsElement?.innerHTML.trim() ?? "";
}

function renderRecommendFrame(frame: HTMLElement, placementResultsHtml: string): void {
  frame.innerHTML = placementResultsHtml;
  const recommendItemElements = frame.querySelectorAll("[data-search-result-recommend-item]");
  frame.dataset.recommendActualCount = String(recommendItemElements.length);
  frame.querySelectorAll("[data-search-result-recommend-contents]").forEach((contentsElement) => {
    startInviewObservation(contentsInviewEventObserver, pendingInviewContents, contentsElement);
  });
  initSearchResultRecommendCarousel(frame);
}

/** 枠が差し替わると描画先を失うため、fetch開始前・完了後にDOM接続を確認して打ち切る。 */
async function loadSearchResultRecommend(frame: HTMLElement): Promise<void> {
  const url = frame.dataset?.url;
  if (url == null || url === "") {
    console.warn(`data-urlが取得できないため枠を削除します`, { frame });
    frame.remove();
    return;
  }
  if (!frame.isConnected) {
    // 交差検知からloadSearchResultRecommend呼び出しまでの間にDOMから外れていた場合は取得しない
    return;
  }

  try {
    const responseHtml = await getRecommendResponseHtml(url);
    if (!frame.isConnected) {
      // 取得完了時点でDOMから外れていた（検索条件変更等で差し替え済み）場合は描画しない
      return;
    }
    const placementResultsHtml = extractPlacementResultsHtml(responseHtml, frame.dataset.recommendPlacementNumber ?? "1");
    if (placementResultsHtml === "") {
      removeRecommendFrame(frame);
      return;
    }
    renderRecommendFrame(frame, placementResultsHtml);
  } catch (error) {
    console.error(`検索結果レコメンド取得失敗:`, error);
    if (frame.isConnected) {
      removeRecommendFrame(frame);
    }
  }
}

async function fetchRecommendResponseHtml(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`レスポンスがNGです: ${url} (${response.status})`);
  }
  return response.text();
}

// 非同期更新後の再初期化用にグローバル公開
window.Nifty = window.Nifty || {};
window.Nifty.Buy = window.Nifty.Buy || {};
window.Nifty.Buy.SearchResultRecommend = window.Nifty.Buy.SearchResultRecommend || {};
window.Nifty.Buy.SearchResultRecommend.init = initBuySearchResultRecommend;

document.addEventListener("DOMContentLoaded", function () {
  initBuySearchResultRecommend();
});

